import { DOCUMENT } from '@angular/common';
import { Component, HostBinding, Inject, Input, OnChanges, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { BasicService } from 'src/app/shared/services/basic.service';
import { Task } from 'src/app/tasks/shared/models';

import { QueueIdEnum, TaskListModeEnum } from '../shared/enums';
import { TaskListConfiguration } from '../shared/models';
import { ArchiveDialogComponent } from './archive-dialog/archive-dialog.component';
import { ExportDialogComponent } from './export-dialog/export-dialog.component';
import { MassActionsService } from './mass-actions.service';
import { MassAnswerDialogComponent } from './mass-answer-dialog/mass-answer-dialog.component';

@Component({
  selector: 'app-mass-actions',
  templateUrl: './mass-actions.component.html',
  styleUrls: ['./mass-actions.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class MassActionsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() loadedTasksIds: number[];
  @Input() taskListMode: TaskListModeEnum;
  @Input() tasksById: { [key: number]: Task };
  @Input() tasksIds: number[];
  @Input() taskListConfiguration: TaskListConfiguration;

  readonly selectionLimit = 1000;
  readonly answerLimit = 20;

  hasOnlyOneSiteTypeSelected = false;
  isAlcmeonUser: boolean;
  isSelectAllDisabled = false;
  isSelectAllChecked = false;
  isSelectAllIndeterminate = false;
  state = '';

  @HostBinding('class.selected') get isVisible() {
    return this.isSelectAllChecked || this.isSelectAllIndeterminate;
  }
  @HostBinding('class') get mode() {
    return this.taskListMode;
  }
  @HostBinding('style') style = '';

  private subscriptions = new Subscription();

  constructor(
    private alcmeonContextService: AlcmeonContextService,
    private basicService: BasicService,
    private dialog: MatDialog,
    public massActionsService: MassActionsService,
    @Inject(DOCUMENT) public document: Document,
    @Inject('$scope') private $scope: any,
    @Inject('$state') private $state: any
  ) {}

  ngOnInit() {
    this.isAlcmeonUser = this.alcmeonContextService.isAlcmeonUser();

    this.subscriptions.add(
      // update the "select all" checkbox when individual task is selected in TaskActionsComponent
      this.massActionsService.selectTaskBS.subscribe(() => {
        this.updateHasOnlyOneTypeSelected();
        this.updateSelectAllCheckboxState();
      })
    );

    const excludedFromSelection = [
      QueueIdEnum.AnsweredByMe,
      QueueIdEnum.AnsweredByOthers,
      QueueIdEnum.ArchivedByMe,
      QueueIdEnum.ArchivedByOthers,
    ];

    if (this.taskListMode === TaskListModeEnum.Inbox) {
      this.state = this.taskListConfiguration.parentName;

      if (excludedFromSelection.includes(this.taskListConfiguration.queueId as QueueIdEnum)) {
        this.isSelectAllDisabled = true;
      }

      this.resizeAndMoveBar();
      this.$scope.$watch(
        () => this.$state.includes('**.process'),
        () => {
          if (this.$state.includes('**.process')) {
            this.cancel();
          } else {
            setTimeout(() => {
              this.resizeAndMoveBar();
            }, 0);
          }
        }
      );
    }
  }

  /**
   * Need to refresh checkbox state when loading new tasks via infinite scroll
   */
  ngOnChanges(): void {
    this.updateSelectAllCheckboxState();
  }

  /**
   * Can't be done with CSS because it's in position absolute and depends on 2 others components.
   *
   * Resize : if task list has scroll bar or not, to align "select all" checkbox with tasks checkboxes
   * Move : get the height of tabs bar (if there are subTabs or not), to be aligned on top
   */
  resizeAndMoveBar() {
    const styles = [];

    const tabs = this.document.getElementsByTagName('angular-tabs-navigation')[0] as HTMLElement;

    if (tabs) {
      const height = tabs.offsetHeight;
      styles.push(`top:-${height}px`);
    }

    const taskList = this.document.getElementsByClassName('task-list')[0] as HTMLElement;

    if (taskList) {
      const scrollBarWidth = taskList.offsetWidth - taskList.clientWidth;
      styles.push(`padding-right:${scrollBarWidth}px`);
    }

    this.style = styles.join(';');
  }

  /**
   * Update the checked state of the "select all" checkbox : checked, indeterminate, unchecked
   */
  updateSelectAllCheckboxState() {
    const selection = this.massActionsService.idsSelection;

    if (selection.length === 0) {
      this.isSelectAllChecked = false;
      this.isSelectAllIndeterminate = false;
    } else if (this.loadedTasksIds.every(id => this.tasksById[id].isNotSelectable || selection.includes(id))) {
      this.isSelectAllChecked = true;
      this.isSelectAllIndeterminate = false;
    } else {
      this.isSelectAllChecked = false;
      this.isSelectAllIndeterminate = true;
    }
  }

  /**
   * Select or unselect loaded (displayed) tasks, except task that are not selectable :
   * Two cases (TaskActionsComponent) :
   * - Task that is archived or answered
   * - Task that belong to another team.
   *
   * It can happen if you make an action on a task but the task list is not reloaded yet.
   * In archived or answered tabs, the checkbox is disabled
   */
  selectLoadedTasks(checked: boolean) {
    // doc says "indeterminate" is set to false when checkbox is clicked, but it seems buggy, unexpected behaviour without this line
    this.isSelectAllIndeterminate = false;

    this.checkAllTasks(checked);

    this.massActionsService.toggleTasks(
      checked,
      this.loadedTasksIds.filter(id => !this.tasksById[id].isNotSelectable)
    );

    this.updateHasOnlyOneTypeSelected();

    if (checked && !this.massActionsService.idsSelection.length) {
      // Timeout is for when you 'select all' tasks that are not selectable. Without it the checkbox stay checked.
      // ex : tasks you just archived/answered without reloading the tab
      setTimeout(() => {
        this.updateSelectAllCheckboxState();
      });
    }
  }

  /**
   * Select all tasks, limited to selectionLimit (1000)
   */
  selectAllTasks() {
    this.isSelectAllIndeterminate = false;
    this.isSelectAllChecked = true;

    this.checkAllTasks(true);

    this.massActionsService.toggleTasks(
      true,
      this.tasksIds
        .slice(0, this.selectionLimit)
        // if the task is not loaded, we can't know if it's not checkable, so we keep it
        .filter(id => !this.tasksById[id] || !this.tasksById[id].isNotSelectable)
    );
  }

  private updateHasOnlyOneTypeSelected() {
    const types = [];
    this.massActionsService.idsSelection.forEach(id => {
      if (this.tasksById[id] && types.indexOf(this.tasksById[id].type) === -1) {
        types.push(this.tasksById[id].type);
      }
    });

    this.hasOnlyOneSiteTypeSelected = types.length === 1 ? true : false;
  }

  cancel() {
    this.isSelectAllIndeterminate = false;
    this.isSelectAllChecked = false;
    this.checkAllTasks(false);
    this.massActionsService.toggleTasks(false);
  }

  private checkAllTasks(checked: boolean) {
    this.loadedTasksIds.forEach(id => {
      if (!this.tasksById[id].isNotSelectable) {
        this.tasksById[id].isChecked = checked;
      }
    });
  }

  answer() {
    const dialogRef = this.dialog.open(MassAnswerDialogComponent, {
      panelClass: ['medium-size'],
      disableClose: true,
      data: {
        taskListConfiguration: this.taskListConfiguration,
        tasksById: this.tasksById,
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.cancel();
      }
    });
  }

  archive() {
    const dialogRef = this.dialog.open(ArchiveDialogComponent, {
      panelClass: ['medium-size'],
      data: { number: this.massActionsService.idsSelection.length },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.basicService.showSpinner();
        this.massActionsService
          .archive(this.massActionsService.idsSelection)
          .pipe(
            finalize(() => {
              this.basicService.hideSpinner();
            })
          )
          .subscribe({
            next: () => {
              this.cancel();
              this.basicService.toastSuccess('inbox/task-list/mass-actions/archive/success');
            },
            error: () => {
              this.basicService.toastAlert('inbox/task-list/mass-actions/archive/error');
            },
          });
      }
    });
  }

  export() {
    const dialogRef = this.dialog.open(ExportDialogComponent, {
      panelClass: ['medium-size'],
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.cancel();
      }
    });
  }

  ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
  }
}
